import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "cuidar-cart-v1";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota
    }
  }, [items]);

  const addItem = (product, size, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.product_id === product.id && i.size === size,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(next[idx].qty + qty, 20) };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          size,
          qty,
          unit_price_cents: product.price_cents,
          image: (product.images || [])[0] || null,
        },
      ];
    });
  };

  const updateQty = (product_id, size, qty) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.product_id === product_id && i.size === size
            ? { ...i, qty: Math.max(1, Math.min(qty, 20)) }
            : i,
        )
        .filter((i) => i.qty > 0),
    );
  };

  const removeItem = (product_id, size) => {
    setItems((prev) =>
      prev.filter((i) => !(i.product_id === product_id && i.size === size)),
    );
  };

  const clear = () => setItems([]);

  const totalQty = useMemo(
    () => items.reduce((s, i) => s + i.qty, 0),
    [items],
  );
  const subtotalCents = useMemo(
    () => items.reduce((s, i) => s + i.qty * i.unit_price_cents, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, totalQty, subtotalCents }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

export function formatBRL(cents) {
  const value = (cents || 0) / 100;
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
